import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';

import { APOLLO_CLIENT_NAMES } from '@customer-portal/shared/constants';
import {
  apolloThrowIfNotSuccess,
  getQueryRootFieldName,
} from '@customer-portal/shared/helpers/custom-operators';

import {
  ADD_NEW_MEMBER_MUTATION,
  REMOVE_MEMBER_MUTATION,
  SEND_WELCOME_EMAIL_MUTATION,
  SETTINGS_MEMBERS_PERMISSIONS_QUERY,
} from '../../graphql';
import { CreateContactRequest, RemoveContactDto } from '../../models';

@Injectable({ providedIn: 'root' })
export class SettingsMembersPermissionsService {
  private clientName = APOLLO_CLIENT_NAMES.contact;

  constructor(private readonly apollo: Apollo) {}

  getSettingsMembersPermissions(
    memberEmail: string,
    accountDnvId: string | null,
  ): Observable<any> {
    const fieldName =
      getQueryRootFieldName(SETTINGS_MEMBERS_PERMISSIONS_QUERY) ||
      'getUserPermissions';

    return this.apollo
      .use(this.clientName)
      .query({
        query: SETTINGS_MEMBERS_PERMISSIONS_QUERY,
        variables: {
          memberEmail,
          accountDNVId: accountDnvId,
        },
        fetchPolicy: 'no-cache',
        context: {
          headers: new HttpHeaders().set('SKIP_LOADING', 'true'),
        },
      })
      .pipe(
        apolloThrowIfNotSuccess(fieldName),
        map((results: any) => results?.data?.getUserPermissions),
      );
  }

  createNewMember(memberDetails: CreateContactRequest): Observable<any> {
    const fieldName = 'createContactDetails';

    return this.apollo
      .use(this.clientName)
      .mutate({
        mutation: ADD_NEW_MEMBER_MUTATION,
        variables: {
          createContactRequest: memberDetails,
        },
      })
      .pipe(
        apolloThrowIfNotSuccess(fieldName),
        map((results: any) => results?.data?.createContactDetails),
      );
  }

  removeMember(
    memberEmail: string,
    selectedCoBrowsingCompanyId: string | null,
  ): Observable<RemoveContactDto> {
    const fieldName =
      getQueryRootFieldName(REMOVE_MEMBER_MUTATION) || 'removeContact';

    return this.apollo
      .use(this.clientName)
      .mutate<{ removeContact: RemoveContactDto }>({
        mutation: REMOVE_MEMBER_MUTATION,
        variables: {
          removeContactRequest: {
            email: memberEmail,
            companyId: selectedCoBrowsingCompanyId,
          },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        apolloThrowIfNotSuccess(fieldName),
        map((results: any) => results?.data?.removeContact),
      );
  }

  sendWelcomeEmail(email: string): Observable<any> {
    const fieldName = 'retriggerOnboarding';

    return this.apollo
      .use(this.clientName)
      .mutate({
        mutation: SEND_WELCOME_EMAIL_MUTATION,
        variables: {
          email,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        apolloThrowIfNotSuccess(fieldName),
        map((results: any) => results?.data?.retriggerOnboarding),
      );
  }
}
